import React, { useEffect, useState } from 'react'
import { Modal, Form, Input, Select, Card, Space, Button } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { Product } from '../types/product.types'
import { DocumentUpload } from '../../../components/DocumentUpload'
import categoryService from '../../categories/services/category.service'
import brandService from '../../brands/services/brand.service'
import { unitService } from '../services/unit.service'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Switch, InputNumber } from 'antd'
import { useProductAttributes } from '../hooks/useProductAttributes'

interface ProductFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => void
    initialValues?: Product | null
    isLoading?: boolean
}

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    initialValues,
    isLoading = false
}) => {
    const [form] = Form.useForm()
    const [isImageUploading, setIsImageUploading] = useState(false)

    // Fetch Categories
    const { data: categories = [] } = useQuery({
        queryKey: ['categories'],
        queryFn: categoryService.getCategories,
        enabled: open,
    })

    // Fetch Brands
    const { data: brands = [] } = useQuery({
        queryKey: ['brands'],
        queryFn: brandService.getBrands,
        enabled: open,
    })

    // Fetch Units
    const { data: units = [] } = useQuery({
        queryKey: ['units'],
        queryFn: unitService.getUnits,
        enabled: open,
    })

    // Fetch global attributes
    const { attributes: globalAttributes, loading: isAttributesLoading } = useProductAttributes()

    useEffect(() => {
        if (open) {
            if (initialValues) {
                form.setFieldsValue({
                    ...initialValues,
                    category_id: initialValues.category_id,
                    default_unit_id: initialValues.default_unit_id,
                    specifications: initialValues.specifications || [],
                    warehouses: initialValues.warehouses || [],
                    attributes: (initialValues as any).productAttributes?.map((attr: any) => ({
                        id: attr.id,
                        is_required: attr.pivot?.is_required ?? true,
                        sort_order: attr.pivot?.sort_order ?? 0
                    })) || []
                })
            } else {
                form.resetFields()
                form.setFieldsValue({ status: 'active', warehouses: [], attributes: [] })
            }
        }
    }, [open, initialValues, form])

    const handleOk = () => {
        form.validateFields().then((values) => {
            const payload = {
                ...values,
                category_id: values.category_id,
                base_price: initialValues?.price || 0,
                stock_quantity: initialValues?.stock || 0,
                currency: 'INR',
            }
            onSubmit(payload)
        })
    }

    return (
        <Modal
            title={initialValues ? "Edit Product" : "Add New Product"}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            width={700}
            confirmLoading={isLoading}
            okButtonProps={{ disabled: isImageUploading }}
            okText={isImageUploading ? 'Uploading image...' : initialValues ? 'Update Product' : 'Create Product'}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Product Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter product name' }]}
                >
                    <Input placeholder="Enter product name" />
                </Form.Item>
                <Form.Item
                    label="SKU"
                    name="sku"
                    rules={[{ required: true, message: 'Please enter SKU' }]}
                >
                    <Input placeholder="Enter SKU code" />
                </Form.Item>

                <Form.Item
                    label="Category"
                    name="category_id" // Use category_id to store ID directly
                    rules={[{ required: true, message: 'Please select category' }]}
                >
                    <Select placeholder="Select category" loading={!categories.length}>
                        {categories.map((cat: any) => (
                            <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Brand"
                    name="brand"
                    rules={[{ required: true, message: 'Please select brand' }]}
                >
                    <Select placeholder="Select brand" loading={!brands.length}>
                        {brands.map((brand: any) => (
                            <Select.Option key={brand.id} value={brand.name}>{brand.name}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Default Unit"
                    name="default_unit_id"
                    rules={[{ required: true, message: 'Please select unit' }]}
                >
                    <Select placeholder="Select unit" loading={!units.length}>
                        {units.map((unit: any) => (
                            <Select.Option key={unit.id} value={unit.id}>{unit.name} ({unit.symbol})</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Card size="small" title="Attached Attributes" style={{ marginBottom: 16 }}>
                    <Form.List name="attributes">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'id']}
                                            rules={[{ required: true, message: 'Missing attribute' }]}
                                            style={{ width: 250 }}
                                        >
                                            <Select placeholder="Select Attribute" loading={isAttributesLoading}>
                                                {globalAttributes?.map((attr: any) => (
                                                    <Select.Option key={attr.id} value={attr.id}>
                                                        {attr.name} ({attr.code})
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'sort_order']}
                                            style={{ width: 80 }}
                                        >
                                            <InputNumber min={0} placeholder="Sort" style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'is_required']}
                                            valuePropName="checked"
                                        >
                                            <Switch checkedChildren="Req" unCheckedChildren="Opt" />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add({ sort_order: 0, is_required: true })} block icon={<PlusOutlined />}>
                                        Attach Attribute
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Card size="small" title="Specifications" style={{ marginBottom: 16 }}>
                    <Form.List name="specifications">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'key']}
                                            rules={[{ required: true, message: 'Missing key' }]}
                                        >
                                            <Input placeholder="Key (e.g. Color)" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'value']}
                                            rules={[{ required: true, message: 'Missing value' }]}
                                        >
                                            <Input placeholder="Value (e.g. Grey)" />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Specification
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Card size="small" title="Origin Locations" style={{ marginBottom: 16 }}>
                    <Form.List name="warehouses">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
                                        <Space align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                rules={[{ required: true, message: 'Missing name' }]}
                                                label="Origin Name"
                                                style={{ marginBottom: 0, width: 200 }}
                                            >
                                                <Input placeholder="e.g. Chennai Plant" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'type']}
                                                rules={[{ required: true, message: 'Missing type' }]}
                                                label="Origin Type"
                                                style={{ marginBottom: 0, width: 150 }}
                                            >
                                                <Select placeholder="Select type">
                                                    <Select.Option value="warehouse">Warehouse</Select.Option>
                                                    <Select.Option value="manufacturing_unit">Manufacturing Unit</Select.Option>
                                                </Select>
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />
                                        </Space>
                                        <Space align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'latitude']}
                                                rules={[{ required: true, message: 'Missing latitude' }]}
                                                label="Latitude"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input type="number" step="0.00000001" placeholder="e.g. 13.0827" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'longitude']}
                                                rules={[{ required: true, message: 'Missing longitude' }]}
                                                label="Longitude"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input type="number" step="0.00000001" placeholder="e.g. 80.2707" />
                                            </Form.Item>
                                        </Space>
                                    </div>
                                ))}
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Origin Location
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Form.Item label="Product Images" name="images">
                    <DocumentUpload documentType="product_image" multiple maxCount={5} onUploadingChange={setIsImageUploading} />
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input.TextArea rows={3} placeholder="Enter product description" />
                </Form.Item>
                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true }]}
                >
                    <Select>
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    )
}
